import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ShieldCheck, MapPin, User, Phone, Map, Home, Globe, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import { cn } from '@/lib/utils';
import { PhoneInputWithCountry } from '@/components/ui/PhoneInputWithCountry';
import { validateE164Phone } from '@/constants/countryCodes';

const INDIAN_STATES = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
    "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
    "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
    "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
    "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

export function BillingDetailsModal({ isOpen, onClose, onConfirm, portal, forceShow = false, initialData = null }) {
    const [loading, setLoading] = useState(false);
    const [initialCheckDone, setInitialCheckDone] = useState(false);
    const [billingInfo, setBillingInfo] = useState({
        billing_name: '',
        billing_phone: '',
        phone_no: '',
        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_country: '',
        billing_pincode: '',
        billing_gstin: ''
    });

    useEffect(() => {
        if (isOpen) {
            setInitialCheckDone(false);
            fetchInitialData();
        }
    }, [isOpen, initialData]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);

            if (initialData) {
                setBillingInfo({
                    billing_name: initialData.billing_name || initialData.full_name || '',
                    billing_phone: initialData.billing_phone || initialData.parent_whatsapp_number || initialData.phone_no || '',
                    phone_no: initialData.billing_phone || initialData.parent_whatsapp_number || initialData.phone_no || '',
                    billing_address: initialData.billing_address || '',
                    billing_city: initialData.billing_city || '',
                    billing_state: initialData.billing_state || '',
                    billing_country: initialData.billing_country || 'India',
                    billing_pincode: initialData.billing_pincode || '',
                    billing_gstin: initialData.billing_gstin || ''
                });
                setInitialCheckDone(true);
                setLoading(false);
                return;
            }

            const token = getAuthToken(portal);
            if (!token) {
                setInitialCheckDone(true);
                setLoading(false);
                return;
            }

            const headers = { Authorization: `Bearer ${token}` };

            // 1. Fetch Profile Data (for Name and Phone)
            const profileRes = await axios.get(apiUrl('users/profile/'), { headers });
            const p = profileRes.data;

            // Check if required billing details already exist in user profile
            if (!forceShow && p.billing_state && p.billing_country && (p.billing_phone || p.phone_no)) {
                const existingInfo = {
                    billing_name: p.billing_name || p.full_name || '',
                    billing_phone: p.billing_phone || p.phone_no || '',
                    phone_no: p.billing_phone || p.phone_no || '',
                    billing_address: p.billing_address || '',
                    billing_city: p.billing_city || '',
                    billing_state: p.billing_state || '',
                    billing_country: p.billing_country || '',
                    billing_pincode: p.billing_pincode || '',
                    billing_gstin: p.billing_gstin || ''
                };
                onConfirm(existingInfo);
                return;
            }

            // 2. Fetch Geo Data from IP (for City, State, Country) only if missing
            let geoData = {};
            try {
                const geoRes = await axios.get(apiUrl('users/detect-location/'), { headers });
                geoData = geoRes.data;
            } catch (geoErr) {
                console.warn('Geo detection failed', geoErr);
            }

            setBillingInfo({
                billing_name: p.billing_name || p.full_name || '',
                billing_phone: p.billing_phone || p.phone_no || '',
                phone_no: p.billing_phone || p.phone_no || '',
                billing_address: p.billing_address || '',
                billing_city: p.billing_city || geoData.city || '',
                billing_state: p.billing_state || geoData.state || '',
                billing_country: p.billing_country || geoData.country || 'India',
                billing_pincode: p.billing_pincode || '',
                billing_gstin: p.billing_gstin || ''
            });

            setInitialCheckDone(true);

        } catch (err) {
            console.error('Failed to fetch initial billing data', err);
            setInitialCheckDone(true);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAndConfirm = async () => {
        // Validate mandatory OIDAR fields
        if (!billingInfo.billing_state) {
            toast.error('Please select your state/region for GST compliance');
            return;
        }
        if (!billingInfo.billing_country) {
            toast.error('Please specify your country');
            return;
        }

        const phoneToValidate = billingInfo.billing_phone || billingInfo.phone_no;
        if (phoneToValidate) {
            const valResult = validateE164Phone(phoneToValidate);
            if (!valResult.valid) {
                toast.error(valResult.error || 'Please enter a valid phone number');
                return;
            }
        }

        try {
            setLoading(true);
            const token = getAuthToken(portal);
            if (token) {
                const headers = { Authorization: `Bearer ${token}` };
                await axios.patch(apiUrl('users/profile/'), billingInfo, { headers }).catch(e => console.warn('Profile patch warning', e));
            }

            // Proceed to payment gateway initiation
            onConfirm(billingInfo);
        } catch (err) {
            console.error('Failed to save billing info', err);
            onConfirm(billingInfo);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !initialCheckDone) return null;

    return (
        <Dialog open={isOpen && initialCheckDone} onOpenChange={onClose}>
            <DialogContent className={cn(
                "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground overflow-y-auto p-0 rounded-2xl md:rounded-3xl shadow-2xl transition-all duration-300",
                "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] max-w-lg",
                "h-auto min-h-[400px] md:min-h-[500px]",
                "max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+9rem))]",
                "md:max-h-[85vh]"
            )}>
                <div className="p-6 sm:p-10 space-y-6">
                    <DialogHeader className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-2xl bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] shadow-lg shadow-emerald-500/20">
                                <ShieldCheck className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl font-bold tracking-tight text-foreground">Billing Details</DialogTitle>
                                <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-black opacity-50 mt-0.5">Secure Transaction</p>
                            </div>
                        </div>
                        <DialogDescription className="text-muted-foreground text-xs leading-relaxed bg-[hsl(var(--main-bg))] p-3 rounded-xl border border-[hsl(var(--card-border))]">
                            {billingInfo.billing_country?.toLowerCase() === 'india' ? (
                                <>
                                    To comply with OIDAR GST regulations, we need to verify your billing location.
                                    Taxes will be calculated based on the information provided below.
                                </>
                            ) : (
                                <>
                                    Please provide your billing details to finalize your purchase.
                                    International transactions are treated as zero-rated export of services.
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5">
                        {/* Basic Info Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="billing_name" className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 ml-1">Billing Name <span className="text-destructive">*</span></Label>
                                <div className="relative group">
                                    <Input
                                        id="billing_name"
                                        placeholder="Full Name"
                                        value={billingInfo.billing_name}
                                        onChange={(e) => setBillingInfo({ ...billingInfo, billing_name: e.target.value })}
                                        className="pl-10 text-xs h-11 rounded-xl border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground transition-all shadow-sm"
                                    />
                                    <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-emerald-500 transition-colors" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="billing_phone" className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 ml-1">Billing Mobile Number (Optional)</Label>
                                <PhoneInputWithCountry
                                    value={billingInfo.billing_phone || billingInfo.phone_no}
                                    onChange={(val) => setBillingInfo({ ...billingInfo, billing_phone: val, phone_no: val })}
                                    selectClassName="h-11 border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground"
                                    inputClassName="h-11 border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground"
                                />
                            </div>
                        </div>

                        {/* Address Field */}
                        <div className="space-y-2">
                            <Label htmlFor="billing_address" className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 ml-1">Address (Optional)</Label>
                            <div className="relative group">
                                <Input
                                    id="billing_address"
                                    placeholder="House / Building / Street"
                                    value={billingInfo.billing_address}
                                    onChange={(e) => setBillingInfo({ ...billingInfo, billing_address: e.target.value })}
                                    className="pl-10 text-xs h-11 rounded-xl border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground transition-all shadow-sm"
                                />
                                <Home className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-emerald-500 transition-colors" />
                            </div>
                        </div>

                        {/* City & State Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="billing_city" className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 ml-1">City / District</Label>
                                <div className="relative group">
                                    <Input
                                        id="billing_city"
                                        placeholder="Your City"
                                        value={billingInfo.billing_city}
                                        onChange={(e) => setBillingInfo({ ...billingInfo, billing_city: e.target.value })}
                                        className="pl-10 text-xs h-11 rounded-xl border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground transition-all shadow-sm"
                                    />
                                    <Map className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-emerald-500 transition-colors pointer-events-none" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="billing_state" className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 ml-1">State / Region <span className="text-destructive">*</span></Label>
                                <div className="relative group">
                                    <Input
                                        id="billing_state"
                                        placeholder="Your State"
                                        value={billingInfo.billing_state}
                                        onChange={(e) => setBillingInfo({ ...billingInfo, billing_state: e.target.value })}
                                        className="pl-10 text-xs h-11 rounded-xl border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground transition-all shadow-sm"
                                    />
                                    <Map className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-emerald-500 transition-colors z-10 pointer-events-none" />
                                </div>
                            </div>
                        </div>

                        {/* Country & Pincode Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="billing_country" className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 ml-1">Country</Label>
                                <div className="relative group">
                                    <Input
                                        id="billing_country"
                                        placeholder="Your Country"
                                        value={billingInfo.billing_country}
                                        onChange={(e) => setBillingInfo({ ...billingInfo, billing_country: e.target.value })}
                                        className="pl-10 text-xs h-11 rounded-xl border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground transition-all shadow-sm"
                                    />
                                    <Globe className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-emerald-500 transition-colors pointer-events-none" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="billing_pincode" className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 ml-1">Pincode (Optional)</Label>
                                <div className="relative group">
                                    <Input
                                        id="billing_pincode"
                                        placeholder="600XXX"
                                        value={billingInfo.billing_pincode}
                                        onChange={(e) => setBillingInfo({ ...billingInfo, billing_pincode: e.target.value })}
                                        className="pl-10 text-xs h-11 rounded-xl border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground transition-all shadow-sm"
                                    />
                                    <MapPin className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-emerald-500 transition-colors pointer-events-none" />
                                </div>
                            </div>
                        </div>

                        {/* GSTIN Field */}
                        <div className="space-y-2">
                            <Label htmlFor="billing_gstin" className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 ml-1">GSTIN (Optional)</Label>
                            <Input
                                id="billing_gstin"
                                placeholder="Enter 15-digit GSTIN"
                                value={billingInfo.billing_gstin}
                                onChange={(e) => setBillingInfo({ ...billingInfo, billing_gstin: e.target.value.toUpperCase() })}
                                className="text-xs h-11 rounded-xl border-[hsl(var(--card-border))] bg-[hsl(var(--input-bg))] text-foreground transition-all shadow-sm"
                                maxLength={15}
                            />
                        </div>
                    </div>

                    <DialogFooter className="flex-col sm:flex-row gap-3 pt-2">
                        <Button
                            variant="ghost"
                            onClick={onClose}
                            className="flex-1 text-xs h-12 rounded-2xl border-none hover:bg-[hsl(var(--sidebar-hover))] text-muted-foreground font-semibold"
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveAndConfirm}
                            className="flex-[2] bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-black text-xs h-12 rounded-2xl shadow-xl shadow-emerald-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] border-0"
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Confirm & Proceed
                        </Button>
                    </DialogFooter>

                    <div className="pt-2 text-center">
                        <p className="text-[9px] text-muted-foreground flex items-center justify-center gap-1.5 opacity-50 italic">
                            <ShieldCheck className="w-2.5 h-2.5" />
                            Secure processing powered by industry standards.
                        </p>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
